import { Action } from 'redux';
import { eventChannel, EventChannel } from 'redux-saga';
import { ForkEffect } from 'redux-saga/effects';
import { call, getContext, put, race, take, takeEvery } from 'typed-redux-saga';
import { followWithRetry } from '../utils/followWithRetry';
import type { SubscriptionFollowOutcome } from '../types/SubscriptionFollow.types';
import { RobotCleaningProgressActions } from './robotCleaningProgressSlice';
import { getCv50TaskProgress } from './utils/taskProgress';
import type {
  CloseRobotCleaningProgressAction,
  OpenRobotCleaningProgressRequestAction,
} from './robotCleaningProgressActions.types';
import type { IDependencies } from 'app/cross-cutting-concerns/dependency-injection/interfaces/IDependencies';
import type {
  CleaningTaskProgressSubscriptionResponse,
  CleaningTaskSubscriptionResponse,
} from 'app/modules/machine-inventory/RobotCleaningControlClient';

type CleaningTaskUpdate = NonNullable<CleaningTaskSubscriptionResponse['cleaningTask']>;

type ProgressChannelEvent =
  | { kind: 'progress'; payload: CleaningTaskProgressSubscriptionResponse['cleaningTaskProgress'] }
  | { kind: 'task' }
  | { kind: 'taskUpdated'; payload: CleaningTaskUpdate }
  | { kind: 'error'; error: unknown };

// Progress is per task, and a schedule starting later creates a *new* task. So besides the progress
// subscription this watches the device's current task, and re-points the progress subscription at
// the new task id (emitting 'task' so the saga can refetch its progress).
const createProgressChannel = (
  service: IDependencies['robotCleaningControlService'],
  materialNumber: string,
  serialNumber: string,
  initialDeviceTaskId: string | null
): EventChannel<ProgressChannelEvent> =>
  eventChannel<ProgressChannelEvent>(emit => {
    let currentDeviceTaskId = initialDeviceTaskId;
    let progressSubscription: { unsubscribe: () => void } | undefined;

    const subscribeToProgress = (deviceTaskId: string | null): void => {
      progressSubscription?.unsubscribe();
      progressSubscription = undefined;

      if (!deviceTaskId) {
        return;
      }

      progressSubscription = service.subscribeToCleaningTaskProgress({ deviceTaskId }).subscribe({
        next: result => {
          if (result.data?.cleaningTaskProgress) {
            emit({ kind: 'progress', payload: result.data.cleaningTaskProgress });
          }
        },
        error: error => emit({ kind: 'error', error }),
      });
    };

    subscribeToProgress(currentDeviceTaskId);

    const taskSubscription = service.subscribeToCleaningTask({ materialNumber, serialNumber }).subscribe({
      next: result => {
        const task = result.data?.cleaningTask;
        const deviceTaskId = task?.id;

        if (deviceTaskId && deviceTaskId !== currentDeviceTaskId) {
          currentDeviceTaskId = deviceTaskId;
          subscribeToProgress(deviceTaskId);
          emit({ kind: 'task' });
        } else if (task && deviceTaskId === currentDeviceTaskId) {
          // Other updates of the same task (status, route, map) don't change what progress to follow,
          // but the control buttons need them to tell when a command has taken effect.
          emit({ kind: 'taskUpdated', payload: task });
        }
      },
      error: error => emit({ kind: 'error', error }),
    });

    return () => {
      taskSubscription.unsubscribe();
      progressSubscription?.unsubscribe();
    };
  });

// Loads and follows the current task; throws if the initial fetch fails.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* followCurrentTask(
  robotCleaningControlService: IDependencies['robotCleaningControlService'],
  { robotId, materialNumber, serialNumber }: OpenRobotCleaningProgressRequestAction['payload'],
  isCloseForThisRobot: (closeAction: Action) => closeAction is CloseRobotCleaningProgressAction
): Generator<unknown, SubscriptionFollowOutcome, any> {
  // Raced so a close during the fetch isn't missed, leaving a subscription nothing stops.
  const { task, closed } = yield* race({
    task: call(getCv50TaskProgress, robotCleaningControlService, materialNumber, serialNumber),
    closed: take(isCloseForThisRobot),
  });

  if (closed) {
    return { kind: 'stopped' };
  }

  yield* put(
    RobotCleaningProgressActions.openRobotCleaningProgressSuccess({
      robotId,
      deviceTaskId: task?.deviceTaskId ?? null,
      progress: task?.progress ?? 0,
      name: task?.name ?? null,
      subName: task?.subName ?? null,
      taskStatus: task?.taskStatus ?? null,
      mapId: task?.mapId ?? null,
      step: task?.step ?? null,
      stepCount: task?.stepCount ?? null,
      estimatedEndTime: task?.estimatedEndTime ?? null,
    })
  );

  // Opened even without a current task: a schedule may start one while the panel is open.
  const channel = yield* call(
    createProgressChannel,
    robotCleaningControlService,
    materialNumber,
    serialNumber,
    task?.deviceTaskId ?? null
  );

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { event } = yield* race({
        closed: take(isCloseForThisRobot),
        event: take(channel),
      });

      if (!event) {
        return { kind: 'stopped' };
      }

      if (event.kind === 'error') {
        console.error(event.error);

        return { kind: 'failed', error: event.error };
      }

      if (event.kind === 'progress') {
        yield* put(
          RobotCleaningProgressActions.robotCleaningProgressUpdated({
            robotId,
            progress: Math.min(Math.max(event.payload.progress ?? 0, 0), 100),
            step: event.payload.step,
            stepCount: event.payload.stepCount,
            estimatedEndTime: event.payload.stepEstimatedEndTime,
          })
        );
      } else if (event.kind === 'taskUpdated') {
        yield* put(
          RobotCleaningProgressActions.robotCleaningTaskUpdated({
            robotId,
            name: event.payload.name,
            subName: event.payload.subName,
            taskStatus: event.payload.status,
            mapId: event.payload.mapId,
          })
        );
      } else {
        // New task: show its progress. A failed refetch keeps the loop; the channel already follows it.
        try {
          // Raced so a close during the refetch doesn't re-create the deleted entry.
          const { nextTask, closed: closedDuringRefetch } = yield* race({
            nextTask: call(getCv50TaskProgress, robotCleaningControlService, materialNumber, serialNumber),
            closed: take(isCloseForThisRobot),
          });

          if (closedDuringRefetch) {
            return { kind: 'stopped' };
          }

          yield* put(
            RobotCleaningProgressActions.openRobotCleaningProgressSuccess({
              robotId,
              deviceTaskId: nextTask?.deviceTaskId ?? null,
              progress: nextTask?.progress ?? 0,
              name: nextTask?.name ?? null,
              subName: nextTask?.subName ?? null,
              taskStatus: nextTask?.taskStatus ?? null,
              mapId: nextTask?.mapId ?? null,
              step: nextTask?.step ?? null,
              stepCount: nextTask?.stepCount ?? null,
              estimatedEndTime: nextTask?.estimatedEndTime ?? null,
            })
          );
        } catch (error) {
          console.error(error);
        }
      }
    }
  } finally {
    channel.close();
  }
}

// One instance per robotId (takeEvery), closed only by that robotId's close action.
// Retries on failure, keeping the last known task.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* openRobotCleaningProgressSaga(
  action: OpenRobotCleaningProgressRequestAction
): Generator<unknown, void, any> {
  const { robotCleaningControlService } = yield* getContext<IDependencies>('dependencies');
  const { robotId } = action.payload;

  const isCloseForThisRobot = (closeAction: Action): closeAction is CloseRobotCleaningProgressAction =>
    RobotCleaningProgressActions.closeRobotCleaningProgress.match(closeAction) &&
    closeAction.payload.robotId === robotId;

  yield* followWithRetry({
    follow: () => followCurrentTask(robotCleaningControlService, action.payload, isCloseForThisRobot),
    stop: isCloseForThisRobot,
    onErrorBeforeFirstLoad: function* showError(error) {
      yield* put(RobotCleaningProgressActions.openRobotCleaningProgressError({ robotId, error }));
    },
  });
}

export function* robotCleaningProgressSaga(): Generator<ForkEffect<never>, void> {
  yield* takeEvery(RobotCleaningProgressActions.openRobotCleaningProgressRequest, openRobotCleaningProgressSaga);
}
